<template>
  <div class="dvs-screen">
    <DVSHeader
      v-if="currentStep < 5"
      brand="VENA Y SCRAP"
      :step="currentStep"
      :total-steps="5"
    />

    <main class="dvs-content" :class="{ 'dvs-content--success': currentStep === 5 }">
      <DVSStep1Registro
        v-if="currentStep === 1"
        v-model="form"
        :operadores="operadores"
        :maquinas="maquinas"
        :errors="errors"
        @field-change="handleFieldChange"
      />

      <DVSStep2Produccion
        v-else-if="currentStep === 2"
        v-model="form"
        :productos="productos"
        :colores="colores"
        :errors="errors"
        @field-change="handleFieldChange"
      />

      <DVSStep3Material
        v-else-if="currentStep === 3"
        v-model="form"
        :errors="errors"
        @field-change="handleFieldChange"
      />

      <DVSStep4Resumen v-else-if="currentStep === 4" :form="form" />

      <DVSStep5Confirmacion v-else-if="currentStep === 5" :form="form" />
    </main>

    <DVSBottomBar
      v-if="currentStep < 5"
      :show-back="canGoBack"
      :next-label="buttonLabel"
      :next-icon="buttonIcon"
      :loading="isSubmitting"
      @back="goBack"
      @next="handleNext"
    />

    <DVSBottomBar
      v-else
      next-label="Nuevo Reporte"
      next-icon="bi-arrow-repeat"
      @next="handleNewRecord"
    />
  </div>
</template>

<script setup>
import { computed, ref, onMounted } from "vue";

import DVSHeader from "./components/DVSHeader.vue";
import DVSBottomBar from "./components/DVSBottomBar.vue";
import DVSStep1Registro from "./steps/DVSStep1Registro.vue";
import DVSStep2Produccion from "./steps/DVSStep2Produccion.vue";
import DVSStep3Material from "./steps/DVSStep3Material.vue";
import DVSStep4Resumen from "./steps/DVSStep4Resumen.vue";
import DVSStep5Confirmacion from "./steps/DVSStep5Confirmacion.vue";

import Swal from "sweetalert2";
import { useVenaScrapForm } from "./composables/useVenaScrapForm";
import { crearReporteVenaScrap } from "./services/venaScrapApi";
import {
  getOperadoresPublic,
  getMaquinasPublic,
  getProductosPublic,
  getPigmentosPublic,
} from "@/utils/api_vena_scrap";

import "./styles/vena-scrap.css";

const {
  form,
  currentStep,
  canGoBack,
  parseNumber,
  formatKg,
  validateStep,
  buildPayload,
  next,
  back,
  resetForm,
  completeDraft,
} = useVenaScrapForm();

const isSubmitting = ref(false);
const allowStepBypass = false;

const errors = ref({
  fecha: false,
  operador: false,
  maquina: false,
  producto: false,
  color: false,
  venaKg: false,
  scrapKg: false,
});

const operadores = ref([]);
const maquinas = ref([]);
const productos = ref([]);
const colores = ref([]);

const cargarCatalogos = async () => {
  try {
    const [ops, maqs, prods, cols] = await Promise.all([
      getOperadoresPublic(),
      getMaquinasPublic(),
      getProductosPublic(),
      getPigmentosPublic(),
    ]);

    // ✅ nombre_completo no nombre
    operadores.value = ops.map((op) => ({
      value: op.id,
      label: op.nombre_completo,
    }));

    // ✅ correcto
    // Orden natural (IMMI 01, 02, ..., 10) en vez del orden por id que envía el backend
    maquinas.value = [...maqs]
      .sort((a, b) => a.nombre.localeCompare(b.nombre, "es", { numeric: true }))
      .map((m) => ({
        value: m.id,
        label: m.nombre,
      }));

    // ✅ correcto
    productos.value = prods.map((p) => ({
      value: p.id,
      label: p.nombre,
    }));

    // ✅ hexadecimal no hex
    colores.value = cols.map((c) => ({
      value: c.id,
      label: c.nombre,
      hex: c.hexadecimal,
    }));
  } catch (error) {
    console.error("Error al cargar catálogos:", error);
  }
};

onMounted(() => {
  cargarCatalogos();
});

const buttonLabel = computed(() => {
  if (isSubmitting.value) return "Enviando...";
  if (currentStep.value === 4) return "Aceptar y enviar";
  return "Siguiente";
});

const buttonIcon = computed(() => {
  if (currentStep.value === 4) return "bi-send-check";
  return "bi-arrow-right";
});

function goBack() {
  back();
  window.scrollTo({ top: 0, behavior: "smooth" });
}

async function handleNext() {
  if (!allowStepBypass && !validateCurrentStep()) {
    return;
  }

  if (currentStep.value < 4) {
    next();
    window.scrollTo({ top: 0, behavior: "smooth" });
    return;
  }

  if (currentStep.value === 4) {
    await submitReport();
  }
}

async function submitReport() {
  try {
    isSubmitting.value = true;

    const payload = buildPayload();
    const [response] = await Promise.all([
      crearReporteVenaScrap(payload),
      new Promise((resolve) => setTimeout(resolve, 1500)),
    ]);

    form.folio = response?.folio || generarFolioTemporal();
    form.submittedAt = response?.submittedAt || new Date().toISOString();

    next();
    completeDraft();
  } catch (error) {
    console.error("Error al enviar reporte de vena y scrap:", error);

    Swal.fire({
      icon: "error",
      title: "Error al enviar",
      text: "No se pudo enviar el reporte. Verifica tu conexión e intenta de nuevo.",
    });
  } finally {
    isSubmitting.value = false;
  }
}

function generarFolioTemporal() {
  const year = String(new Date().getFullYear()).slice(-2);
  const random = Math.floor(1000 + Math.random() * 9000);
  return `CVS-${year}-${random}`;
}

function handleNewRecord() {
  resetForm();
  resetErrors();
}

function resetErrors() {
  Object.keys(errors.value).forEach((key) => {
    errors.value[key] = false;
  });
}

function handleFieldChange({ field }) {
  if (!field) return;
  errors.value[field] = false;
}

function validateCurrentStep() {
  resetErrors();

  if (currentStep.value === 1) {
    errors.value.fecha = !form.fecha;
    errors.value.operador = !form.operador;
    errors.value.maquina = !form.maquina;
  }

  if (currentStep.value === 2) {
    errors.value.producto = !form.producto;
    errors.value.color = !form.color;
  }

  if (currentStep.value === 3) {
    const venaValida = form.venaKg !== "" && parseNumber(form.venaKg) >= 0;
    const scrapValida = form.scrapKg !== "" && parseNumber(form.scrapKg) >= 0;
    const alMenosUno = venaValida || scrapValida;

    // Solo marca error si el campo tiene algo inválido,
    // o si ambos están vacíos (ninguno cumple el mínimo)
    errors.value.venaKg = !venaValida && !alMenosUno;
    errors.value.scrapKg = !scrapValida && !alMenosUno;
  }

  return validateStep(currentStep.value);
}
</script>
